import { brand } from "../../config/theme";
// React
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { fetchSgCounts } from "../../features/sg/counts";
import { isMenuItemEnabled } from "../../config/appsConfig";

// Contextos
import { useUserStore } from "../../store/userStore";
import { useSessionCheck } from "../../hooks/useSessionCheck";
import { setLoggingOut } from "../../utils/tokenUtils";
import { logoutUser } from "../../pages/login/functions";

// Componentes
import { Header } from "../../components/base/Header";
import { AppsSidebar, useFilteredCategories } from "../../components/base/AppsSidebar";
import { useTheme } from "../../context/ThemeContext";
import type { AppItem } from "../../components/base/AppsSidebar";
import Swal from "sweetalert2";

// Utilidades
import { AppSidebarMenu } from "../../components/base/AppSidebarMenu";

export default function SGLayout() {
  // Estado globales
  const { t } = useTranslation("common");
  const { t: tSg } = useTranslation("sg");
  const { themeMode, isDarkMode, setTheme } = useTheme();
  const filteredCategories = useFilteredCategories();
  const user = useUserStore((state) => state.user);
  const hasRedirected = useRef(false);

  const {
    isExpiringSoon,
    timeRemainingFormatted,
    isExpired,
    isAuthenticated,
    isInitialized,
  } = useSessionCheck({
    warningThreshold: 60 * 60 * 1000,
    autoRefresh: true,
  });

  // Hooks
  const navigate = useNavigate();
  const location = useLocation();

  // Estado local
  const [isAppsSidebarOpen, setIsAppsSidebarOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const { data: counts, isPending: countsLoading } = useQuery({
    queryKey: ["sg-counts"],
    queryFn: fetchSgCounts,
  });
  const usersCount = counts?.users ?? 0;
  const usersLoading = countsLoading;
  const devicesCount = counts?.devices ?? 0;
  const devicesLoading = countsLoading;

  const openAppsSidebar = () => setIsAppsSidebarOpen(true);
  const closeAppsSidebar = () => setIsAppsSidebarOpen(false);

  const handleAppSelect = (app: AppItem) => {
    if (app.id === "sg") {
      closeAppsSidebar();
      return;
    }
    
    if (app.isComingSoon) {
      Swal.fire({
        title: t("appsSidebar.comingSoon"),
        text: t("appsSidebar.comingSoonText", { name: app.name }),
        icon: "info",
        confirmButtonText: t("appsSidebar.understood"),
        confirmButtonColor: brand.primary,
      });
      return;
    }
    
    if (app.isLocked) {
      Swal.fire({
        title: t("appsSidebar.premiumFeature"),
        text: t("appsSidebar.premiumRequired", { name: app.name }),
        icon: "warning",
        confirmButtonText: t("appsSidebar.understood"),
        confirmButtonColor: brand.primary,
      });
      return;
    }
    
    if (app.route) {
      closeAppsSidebar();
      navigate(app.route);
    } else if (app.externalUrl) {
      window.open(app.externalUrl, "_blank", "noopener,noreferrer");
    }
  };

  const menuItems = [
    {
      id: "cuadre",
      label: tSg("menu.cuadre"),
      icon: "ri-dashboard-3-line",
    },
    {
      id: "plataformas",
      label: tSg("menu.plataformas"),
      icon: "ri-stack-line",
    },
    {
      id: "users",
      label: t("sidebarMenu.users"),
      icon: "ri-user-line",
      count: usersCount,
      loading: usersLoading,
    },
    {
      id: "devices",
      label: t("sidebarMenu.equipment"),
      icon: "ri-truck-line",
      count: devicesCount,
      loading: devicesLoading,
    },
    {
      id: "informes",
      label: tSg("menu.informes"),
      icon: "ri-file-chart-line",
    },
  ].filter((item) => isMenuItemEnabled("internos", "sg", item.id));

  useEffect(() => {
    if (
      isInitialized &&
      (isExpired || !isAuthenticated) &&
      !hasRedirected.current
    ) {
      hasRedirected.current = true;
      setLoggingOut(false);
      navigate("/");
    }
  }, [isExpired, isAuthenticated, isInitialized, navigate]);

  useEffect(() => {
    if (!user) {
      if (location.pathname !== "/splash") {
        localStorage.setItem("lastRoute", location.pathname);
        navigate("/splash");
      }
    }
  }, [user, location.pathname, navigate]);

  return (
    <div className={`h-screen flex flex-col ${isDarkMode ? "bg-dark-bg" : "bg-gray-50"}`}>
      {/* Banner de sesión por expirar */}
      {isExpiringSoon && (
        <div className="bg-amber-500 text-white px-4 py-2 text-center text-sm flex items-center justify-center gap-2">
          <i className="ri-alarm-warning-line"></i>
          <span>
            {t("sessionBanner.expiring", { time: timeRemainingFormatted })}
          </span>
        </div>
      )}
      <Header
        title="SG"
        subtitle={t("header.dashboard")}
        onOpenAppsSidebar={openAppsSidebar}
        isDarkMode={isDarkMode}
      />
      <div className="flex flex-1 min-h-0 overflow-hidden relative">
        <AppSidebarMenu
          menuItems={menuItems.map((item) => ({
            ...item,
            path: `/sg/${item.id}`,
          }))}
          isDarkMode={isDarkMode}
          isMobileMenuOpen={false}
          onCloseMobileMenu={() => {}}
          onNavigate={(path) => navigate(path)}
          isActiveRoute={(itemId) =>
            location.pathname.endsWith(itemId) ||
            location.pathname.startsWith(`/sg/${itemId}`)
          }
        />
        {/* Main Content */}
        <div className="flex-1 max-h-full overflow-hidden flex justify-center min-w-0 w-full">
          <Outlet />
        </div>
      </div>
      <AppsSidebar
        isOpen={isAppsSidebarOpen}
        onClose={closeAppsSidebar}
        categories={filteredCategories}
        selectedAppId="sg"
        onAppSelect={handleAppSelect}
        onStoreClick={() => navigate("/store")}
        isDarkMode={isDarkMode}
        currentTheme={themeMode}
        onThemeChange={setTheme}
        userInfo={user ? {
          first_name: user.first_name,
          last_name: user.last_name,
          email: user.email,
          username: user.username,
        } : undefined}
        onProfileClick={() => {
          closeAppsSidebar();
          navigate("/profile");
        }}
        onLogout={async () => {
          setIsLoggingOut(true);
          await logoutUser();
          setIsLoggingOut(false);
        }}
        isLoggingOut={isLoggingOut}
      />
    </div>
  );
}
